import React, { useState, useRef, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { 
  Upload, 
  X, 
  Camera, 
  Image as ImageIcon,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const ImageUpload = ({
  value,
  onChange,
  onError,
  accept = 'image/*',
  maxSize = 5 * 1024 * 1024, // 5MB
  aspectRatio = null,
  className = '',
  placeholder = 'Click to upload image',
  preview = true,
  crop = false,
  disabled = false,
  required = false,
  ...props
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = useCallback((file) => {
    if (!file) return false;

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return false;
    }

    // Check file size
    if (file.size > maxSize) {
      setError(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`);
      return false;
    }

    return true;
  }, [maxSize]);

  const processFile = useCallback(async (file) => {
    if (!validateFile(file)) return;

    setIsUploading(true);
    setError(null);

    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      // If crop is enabled, we could integrate with a cropping library here
      // For now, we'll just use the original file
      const processedFile = file;

      // Simulate upload (replace with actual upload logic)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Convert to base64 for storage (or upload to server and get URL)
      const reader = new FileReader();
      reader.onload = (e) => {
        onChange(e.target.result);
        setIsUploading(false);
      };
      reader.readAsDataURL(processedFile);

    } catch (err) {
      setError('Failed to process image');
      setIsUploading(false);
      onError?.(err);
    }
  }, [validateFile, onChange, onError]);

  const handleFileSelect = useCallback((file) => {
    if (disabled) return;
    processFile(file);
  }, [disabled, processFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [disabled, handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragActive(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleClick = useCallback(() => {
    if (disabled) return;
    fileInputRef.current?.click();
  }, [disabled]);

  const handleRemove = useCallback((e) => {
    e.stopPropagation();
    onChange(null);
    setError(null);
  }, [onChange]);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'relative border-2 border-dashed rounded-lg transition-colors cursor-pointer',
          'hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
          dragActive ? 'border-blue-400 bg-blue-50' : 'border-input',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
          error ? 'border-red-300 bg-red-50' : '',
          value ? 'border-green-300 bg-green-50' : '',
          className
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        {...props}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
          required={required}
        />

        {value ? (
          <div className="relative p-4">
            {preview && (
              <div className="relative">
                <img
                  src={value}
                  alt="Preview"
                  className={cn(
                    'w-full h-32 object-cover rounded-md',
                    aspectRatio && `aspect-${aspectRatio}`
                  )}
                />
                {!disabled && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            
            <div className="mt-2 text-center">
              <div className="flex items-center justify-center text-sm text-green-600">
                <CheckCircle className="h-4 w-4 mr-1" />
                Image uploaded successfully
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center">
            {isUploading ? (
              <div className="space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                <p className="text-sm text-muted-foreground">Uploading...</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="mx-auto h-12 w-12 text-muted-foreground">
                  {dragActive ? (
                    <Upload className="h-full w-full" />
                  ) : (
                    <ImageIcon className="h-full w-full" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {dragActive ? 'Drop image here' : placeholder}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG, GIF up to {Math.round(maxSize / 1024 / 1024)}MB
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="absolute inset-0 bg-red-50 bg-opacity-90 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
